import React, {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {useTheme} from '@material-ui/core/styles';
import SectionCard from './SectionCard';
import StatusPill from './StatusPill';
import settingsStyles from '../settingsStyles';
import {formatDateRange} from '../inventoryGaps';

/**
 * Kontenjan bosluklari: sitede satilamayan oda / tarih araliklari.
 *
 * Veri /property/rooms-inventory/get ucundan, channel_id = 1 (Booking Engine) ile gelir —
 * Kontenjan ekraninin kullandigi ucun aynisi. `gaps` inventoryGaps.js tarafindan uretilir:
 *   [{roomId, roomName, emptyDays, totalDays, ranges: [{from, to, days, reason}]}]
 *
 * `hasData` false iken "kontenjan tanimli" denmez; veri okunamadigi ayrica belirtilir.
 */
export default function InventoryGapsCard({
    gaps = [],
    dayRange = 30,
    fetching,
    hasData,
    inventoryUrl,
    windowStart,
    windowEnd,
    onEditInventory,
    onExport,
}) {
    const {t} = useTranslation();
    const classes = settingsStyles();
    const theme = useTheme();
    // Dar ekranda tablo yerine oda bazli liste gosterilir
    const compactList = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);

    const affected = gaps.filter((gap) => gap.emptyDays > 0);
    const totalEmptyDays = affected.reduce((sum, gap) => sum + (gap.emptyDays || 0), 0);

    // Listede gosterilecek toplam donem sayisi — liste butonunun gorunurlugunu belirler
    const totalRangeCount = affected.reduce((sum, gap) => sum + (gap.ranges || []).length, 0);

    // Tum donemlerin nedeni ayniysa "Neden" sutunu her satirda ayni degeri tekrarlar;
    // bilgi tasimayan bu sutun gizlenip neden alt nota tasinir.
    const reasonSet = new Set();
    affected.forEach((gap) => (gap.ranges || []).forEach((range) => reasonSet.add(range.reason)));
    const singleReason = reasonSet.size === 1 ? Array.from(reasonSet)[0] : null;

    const reasonLabel = (reason) => (reason === 'closed'
        ? t('enw-web-settings-reason_closed', 'Satışa kapalı')
        : t('enw-web-settings-reason_zero', 'Kontenjan girilmemiş'));

    return (
        <SectionCard
            id="kontenjan"
            icon="fas fa-calendar-times"
            title={t('enw-web-settings-inventory_title', 'Kontenjan Boşlukları')}
            description={windowStart && windowEnd
                ? `${formatDateRange(windowStart, windowEnd)} · ${t('enw-web-settings-inventory_desc_short', 'sitede satılamayan oda ve tarihler')}`
                : t('enw-web-settings-inventory_desc', {
                    defaultValue: 'Önümüzdeki {{days}} gün · sitede satılamayan oda ve tarihler',
                    days: dayRange,
                })}
            actions={
                totalRangeCount > 0 && (
                    <Button className={classes.btn} onClick={() => setOpen(true)} startIcon={<i className="fas fa-list" />}>
                        {t('enw-web-settings-inventory_open_list', 'Listeyi Aç')}
                    </Button>
                )
            }
        >
            {fetching ? (
                <div className={classes.loadingBox}>
                    <CircularProgress size={28} style={{color: '#d71f26'}} />
                </div>
            ) : (
                <>
                    {!hasData ? (
                        // Veri okunamadiginda "sorun yok" denmez — durum bilinmiyor olarak gosterilir.
                        <div className={classes.alertBar} style={{background: '#eef1f6'}}>
                            <i className="fas fa-info-circle" style={{color: '#8a95a5'}} />
                            <p className={classes.alertText}>
                                {t('enw-web-settings-inventory_no_data', 'Kontenjan bilgisi alınamadı. Oda ve fiyat tanımlarınızı Kontenjan ekranından kontrol edin.')}
                            </p>
                            {inventoryUrl && (
                                <Button className={classes.btn} component={Link} to={inventoryUrl} style={{marginLeft: 'auto'}}>
                                    {t('enw-web-settings-inventory_calendar', 'Kontenjan Takvimi')}
                                </Button>
                            )}
                        </div>
                    ) : affected.length > 0 ? (
                        <div className={classes.alertBar}>
                            <i className="fas fa-exclamation-triangle" />
                            <p className={classes.alertText}>
                                {t('enw-web-settings-inventory_alert', {
                                    defaultValue: '{{rooms}} oda tipinde {{days}} gün satışa kapalı. Bu tarihlerde site rezervasyon alamıyor.',
                                    rooms: affected.length,
                                    days: totalEmptyDays,
                                })}
                            </p>
                            {inventoryUrl && (
                                <Button className={classes.btn} component={Link} to={inventoryUrl} style={{marginLeft: 'auto'}}>
                                    {t('enw-web-settings-inventory_calendar', 'Kontenjan Takvimi')}
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className={classes.alertBar} style={{background: '#e6f4ec'}}>
                            <i className="fas fa-check-circle" style={{color: '#157f4f'}} />
                            <p className={classes.alertText}>
                                {t('enw-web-settings-inventory_clean', {
                                    defaultValue: 'Önümüzdeki {{days}} gün için tüm oda tiplerinde kontenjan tanımlı.',
                                    days: dayRange,
                                })}
                            </p>
                        </div>
                    )}

                    {gaps.length > 0 && (
                        <>
                            <div className={classes.gapList}>
                                {gaps.map((gap) => {
                                    const total = gap.totalDays || dayRange;
                                    const empty = gap.emptyDays || 0;
                                    const emptyPercent = total ? Math.min(100, (empty / total) * 100) : 0;
                                    return (
                                        <div className={classes.gapLine} key={gap.roomId || gap.roomName}>
                                            <span className={classes.gapRoom} title={gap.roomName}>{gap.roomName}</span>
                                            <span className={classes.gapTrack}>
                                                <i className={classes.gapFull} style={{width: `${100 - emptyPercent}%`}} />
                                                <i className={classes.gapEmpty} style={{width: `${emptyPercent}%`}} />
                                            </span>
                                            <span className={classes.gapCount}>
                                                {empty > 0
                                                    ? t('enw-web-settings-day_count', {defaultValue: '{{count}} gün', count: empty})
                                                    : '—'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className={classes.legend}>
                                <span className={classes.legendItem}>
                                    <i className={classes.legendSwatch} style={{background: '#157f4f'}} />
                                    {t('enw-web-settings-legend_available', 'Kontenjan var')}
                                </span>
                                <span className={classes.legendItem}>
                                    <i className={classes.legendSwatch} style={{background: '#a86200'}} />
                                    {t('enw-web-settings-legend_empty', 'Kontenjan yok')}
                                </span>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Tam ekran degil: liste kisa oldugunda mobilde koca bos alan kaliyordu.
                Icerik kadar yukseklik alir, uzun listede DialogContent kendi icinde kayar. */}
            {/* Genislik md'de sabit; sutunlarin yayilmasi genisligi daraltarak degil,
                tarih/gun sutunlarina sabit genislik vererek engellenir (asagida). */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="md"
                fullWidth
                classes={{paper: classes.dialogPaper}}
            >
                <MuiDialogTitle disableTypography className={classes.dialogHead}>
                    <div>
                        <Typography variant="h6" className={classes.dialogTitle}>
                            {t('enw-web-settings-inventory_dialog_title', 'Kontenjansız Oda ve Tarihler')}
                        </Typography>
                        <p className={classes.dialogSub}>
                            {formatDateRange(windowStart, windowEnd)}
                            {' · '}
                            {t('enw-web-settings-inventory_dialog_sub', {
                                defaultValue: '{{rooms}} oda tipi, toplam {{days}} gün',
                                rooms: affected.length,
                                days: totalEmptyDays,
                            })}
                        </p>
                    </div>
                    <IconButton
                        aria-label={t('btn-close', 'Kapat')}
                        onClick={() => setOpen(false)}
                        className={classes.dialogClose}
                    >
                        <CloseIcon className="close-button" />
                    </IconButton>
                </MuiDialogTitle>
                <DialogContent style={{padding: "10px 0px"}}>
                    {compactList ? (
                        // Dar ekranda tablo sikisiyordu (Neden sutunu kesiliyor, tarih
                        // araligi alt alta bolunuyordu) — oda bazli liste gosterilir.
                        <div className={classes.gapCardList}>
                            {affected.map((gap) => (
                                <div className={classes.gapCard} key={gap.roomId || gap.roomName}>
                                    <div className={classes.gapCardRoom}>
                                        {gap.roomName}
                                        <span className={classes.gapCardRoomDays}>
                                            {t('enw-web-settings-day_count', {
                                                defaultValue: '{{count}} gün',
                                                count: gap.emptyDays,
                                            })}
                                        </span>
                                    </div>
                                    {(gap.ranges || []).map((range, key) => (
                                        <div className={classes.gapCardItem} key={key}>
                                            <span className={classes.gapCardDate}>
                                                {formatDateRange(range.from, range.to)}
                                            </span>
                                            <span className={classes.gapCardMeta}>
                                                {/* Neden tek tipse alt notta bir kez yazilir */}
                                                {!singleReason && (
                                                    <StatusPill
                                                        tone={range.reason === 'closed' ? 'off' : 'warn'}
                                                        label={reasonLabel(range.reason)}
                                                    />
                                                )}
                                                {gap.totalDays > 0 && range.days >= gap.totalDays && (
                                                    <span className={classes.wholeRangeTag}>
                                                        {t('enw-web-settings-whole_range', 'Tüm Dönem')}
                                                    </span>
                                                )}
                                                <span className={classes.gapCardDays}>
                                                    {t('enw-web-settings-day_count', {
                                                        defaultValue: '{{count}} gün',
                                                        count: range.days,
                                                    })}
                                                </span>
                                            </span>
                                            {typeof onEditInventory === 'function' && (
                                                <button
                                                    type="button"
                                                    className={classes.linkish}
                                                    onClick={() => onEditInventory(range)}
                                                >
                                                    {t('btn-edit')}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                    <div className={classes.tableWrap}>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th className={`${classes.th} ${classes.thSticky}`}>
                                        {t('enw-web-settings-room_type', 'Oda tipi')}
                                    </th>
                                    {/* Tarih ve gun sabit genislikte: oda tipi kalan alani alir,
                                        boylece tarih ile gun arasi acilmaz ve satir takibi kolay kalir. */}
                                    <th className={`${classes.th} ${classes.thSticky}`} style={{width: 300}}>
                                        {t('enw-web-settings-date_range', 'Tarih aralığı')}
                                    </th>
                                    <th className={`${classes.th} ${classes.thSticky}`} style={{textAlign: 'right', width: 88}}>
                                        {t('enw-web-settings-days', 'Gün')}
                                    </th>
                                    {!singleReason && (
                                        <th className={`${classes.th} ${classes.thSticky}`}>
                                            {t('enw-web-settings-reason', 'Neden')}
                                        </th>
                                    )}
                                    {typeof onEditInventory === 'function' && (
                                        <th className={`${classes.th} ${classes.thSticky}`} />
                                    )}
                                </tr>
                            </thead>
                            {/* Oda adi rowSpan ile bir kez yazilir: hem tekrar olmaz hem de
                                hangi satirlarin hangi odaya ait oldugu hucre yuksekliginden okunur. */}
                            <tbody>
                                {affected.map((gap, gapIndex) => (
                                    <Fragment key={gap.roomId || gap.roomName}>
                                        {(gap.ranges || []).map((range, index) => (
                                            <tr
                                                key={index}
                                                className={index === 0 && gapIndex > 0 ? classes.trGroupStart : ''}
                                            >
                                                {index === 0 && (
                                                    <td
                                                        className={`${classes.td} ${classes.tdRoom}`}
                                                        rowSpan={(gap.ranges || []).length}
                                                    >
                                                        <span className={classes.gapRoomName} title={gap.roomName}>
                                                            {gap.roomName}
                                                        </span>
                                                        {(gap.ranges || []).length > 1 && (
                                                            <span className={classes.gapRoomCount}>
                                                                {t('enw-web-settings-range_count', {
                                                                    defaultValue: '{{count}} ayrı dönem',
                                                                    count: gap.ranges.length,
                                                                })}
                                                            </span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className={`${classes.td} ${classes.tdMuted}`}>
                                                    {formatDateRange(range.from, range.to)}
                                                    {gap.totalDays > 0 && range.days >= gap.totalDays && (
                                                        <span className={classes.wholeRangeTag}>
                                                            {t('enw-web-settings-whole_range', 'Tüm Dönem')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={`${classes.td} ${classes.tdMuted}`} style={{textAlign: 'right'}}>
                                                    {range.days}
                                                </td>
                                                {!singleReason && (
                                                    <td className={classes.td}>
                                                        <StatusPill
                                                            tone={range.reason === 'closed' ? 'off' : 'warn'}
                                                            label={reasonLabel(range.reason)}
                                                        />
                                                    </td>
                                                )}
                                                {typeof onEditInventory === 'function' && (
                                                    <td className={classes.td} style={{textAlign: 'right'}}>
                                                        <button
                                                            type="button"
                                                            className={classes.linkish}
                                                            onClick={() => onEditInventory(range)}
                                                        >
                                                            {t('btn-edit')}
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    )}
                </DialogContent>
                <DialogActions className={classes.dialogFoot}>
                    <span className={classes.dialogFootNote}>
                        {singleReason ? (
                            <>
                                <StatusPill
                                    tone={singleReason === 'closed' ? 'off' : 'warn'}
                                    label={reasonLabel(singleReason)}
                                />
                                {' '}
                                {singleReason === 'closed'
                                    ? t('enw-web-settings-inventory_note_closed', '— listelenen tüm tarihler satışa kapalı.')
                                    : t('enw-web-settings-inventory_note_zero', '— listelenen tüm tarihlerde kontenjan girilmemiş, site rezervasyon alamaz.')}
                            </>
                        ) : (
                            t('enw-web-settings-inventory_dialog_note', 'Kontenjan girilmeyen tarihlerde site rezervasyon alamaz.')
                        )}
                    </span>
                    <Button className={classes.btn} onClick={() => setOpen(false)}>
                        {t('btn-close', 'Kapat')}
                    </Button>
                    {typeof onExport === 'function' && (
                        <Button className={classes.btn} onClick={onExport} startIcon={<i className="fas fa-file-excel" />}>
                            {t('enw-web-settings-export_excel', "Excel'e Aktar")}
                        </Button>
                    )}
                    {inventoryUrl && (
                        <Button
                            className={`${classes.btn} ${classes.btnPrimary}`}
                            component={Link}
                            to={inventoryUrl}
                            startIcon={<i className="fas fa-calendar-alt" />}
                        >
                            {t('enw-web-settings-inventory_calendar', 'Kontenjan Takvimi')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </SectionCard>
    );
}
